import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import spinnerInterceptor from "../interceptors/spinner.interceptor";
import authTokenInterceptor from "../interceptors/auth-token-iw.interceptor";
import { VDMZCCTokenResponse } from "@appetize/iw-common/interfaces/vdmz/vdmz-dto";

const http: AxiosInstance = axios.create({
  baseURL: window.location.origin,
  headers: {
    "Content-Type": "application/json; charset=UTF-8",
    Accept: "application/vnd.appetize+json;version=1"
  }
});

authTokenInterceptor.wrapAxios(http);
spinnerInterceptor.wrapAxios(http);

function updateUser(visitor_id, domain_id, reqBody, showSpinner = true) {
  return http
    .put(`vdmz/domain/${domain_id}/visitor/${visitor_id}`, reqBody, {
      timeout: 30000,
      showSpinner,
      applyToken: "auth_token",
      headerType: "Content-Type",
      headerValue: "application/com.appetize.visitor.visitor-api.visitor.v1.0+json"
    } as AxiosRequestConfig)
    .then((res) => res)
    .catch((err) => {
      if (err.code === "ECONNABORTED") {
        throw 408;
      } else {
        throw 409;
      }
    });
}

function fetchFavorites(visitor_id, domain_id, showSpinner = true, applyToken = true) {
  return http.get(`/vdmz/domain/${domain_id}/visitor/${visitor_id}/favorites`, {
    showSpinner,
    applyToken,
    headerType: "Accept",
    headerValue: "application/com.appetize.visitor.visitor-api.visitor.v1.0+json"
  } as AxiosRequestConfig);
}

function addFavorite(itemId, visitor_id, domain_id, showSpinner = false, applyToken = true) {
  return http.post(
    `/vdmz/domain/${domain_id}/visitor/${visitor_id}/favorites`,
    {
      favorite_item: {
        item_id: itemId.toString(),
        source: "VISITOR_SOURCE_INTERACT_WEB"
      }
    },
    {
      timeout: 2000,
      showSpinner,
      applyToken,
      headerType: "Accept",
      headerValue: "application/com.appetize.visitor.visitor-api.visitor.v1.0+json"
    } as AxiosRequestConfig
  );
}

function removeFavorite(itemId, visitor_id, domain_id, showSpinner = false, applyToken = true) {
  return http.delete(`/vdmz/domain/${domain_id}/visitor/${visitor_id}/favorites`, {
    data: { item_id: itemId.toString() },
    timeout: 2000,
    showSpinner,
    applyToken,
    headerType: "Accept",
    headerValue: "application/com.appetize.visitor.visitor-api.visitor.v1.0+json"
  } as AxiosRequestConfig);
}

function resetPassword(domain_id, email, venue_id, redirect_uri = "", showSpinner = true, applyToken = true) {
  return http.post(
    `/vdmz/domain/${domain_id}/reset_password`,
    {
      domain_id,
      email,
      venue_id,
      redirect_uri
    },
    {
      timeout: 2000,
      showSpinner,
      applyToken,
      headerType: "Accept",
      headerValue: "application/com.appetize.visitor.visitor-api.visitor.v1.0+json"
    } as AxiosRequestConfig
  );
}

function checkForgotPasswordHash(domain_id, visitor_id, hash, showSpinner = true, applyToken = true) {
  return http.get(`/vdmz/domain/${domain_id}/visitor/${visitor_id}/hash/${hash}`, {
    showSpinner,
    applyToken,
    headerType: "Accept",
    headerValue: "application/com.appetize.visitor.visitor-api.visitor.v1.0+json"
  } as AxiosRequestConfig);
}

function patchUserPassword(domain_id, visitor_id, password, showSpinner = true, applyToken = true) {
  const payload = {
    patch_ops: [
      {
        op: "replace",
        path: "/password",
        value: password
      }
    ]
  };
  return http.patch(`/vdmz/domain/${domain_id}/visitor/${visitor_id}`, payload, {
    showSpinner,
    headerType: "Accept",
    applyToken,
    headerValue: "application/com.appetize.visitor.visitor-api.visitor.v1.0+json"
  } as AxiosRequestConfig);
}

function saveCreditCardTokens(payload, domainId, visitorId, showSpinner = true) {
  return http.post(`/domain/${domainId}/visitor/${visitorId}/cc_tokens`, payload, {
    showSpinner,
    applyToken: true
  } as AxiosRequestConfig);
}

function deleteCreditCardToken(gateway_id, token, domainId, visitorId, showSpinner = true) {
  return http.delete(`/domain/${domainId}/visitor/${visitorId}/cc_tokens`, {
    data: { gateway_id, token },
    showSpinner,
    applyToken: true
  } as AxiosRequestConfig);
}

function fetchUserCreditCards(domainId, visitorId, showSpinner = true): Promise<AxiosResponse<VDMZCCTokenResponse>> {
  return http.get(`/domain/${domainId}/visitor/${visitorId}/cc_tokens`, {
    showSpinner,
    applyToken: true
  } as AxiosRequestConfig);
}

function refreshVSToken(applyToken = false, showSpinner = true) {
  return http
    .get("auth/visitor/refresh", {
      showSpinner,
      applyToken,
      headerType: "Content-Type",
      headerValue: "application/com.appetize.visitor.dmz.cc_token.v1.0+json"
    } as AxiosRequestConfig)
    .then((res) => res.data);
}

export default {
  updateUser,
  fetchFavorites,
  addFavorite,
  removeFavorite,
  resetPassword,
  checkForgotPasswordHash,
  patchUserPassword,
  saveCreditCardTokens,
  deleteCreditCardToken,
  fetchUserCreditCards,
  refreshVSToken
};
